import React from 'react';
import { Send } from 'lucide-react';
import { C } from '../../theme.js';
import * as api from '../../api.js';
import { Alert, Button, FieldLabel, Select, TextInput } from '../../components/Primitives.jsx';
import { ColumnsContext, Segmented } from '../StepInspector.jsx';
import { MultiCols } from '../shared/MultiCols.jsx';
import { KVRows } from '../shared/KVRows.jsx';
import { ColSelect } from '../shared/ColSelect.jsx';

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const AUTH_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'bearer', label: 'Bearer token' },
  { value: 'basic', label: 'Basic (user + password)' },
  { value: 'api_key', label: 'API key' },
];
const RECORDS_TOKEN = '{{records}}';
const BOOKKEEPING = new Set(['id', '__rowIndex']);
const PLACEHOLDER_RE = /\{([^{}]+)\}/g;

const mono = "'JetBrains Mono', ui-monospace, monospace";
const textArea = {
  width: '100%', minHeight: 70, resize: 'vertical', boxSizing: 'border-box',
  background: C.input, border: `1px solid ${C.borderStrong}`, borderRadius: 8,
  padding: '8px 11px', color: C.fg1, fontSize: '.8rem', fontFamily: mono, outline: 'none',
};
const pre = {
  margin: '6px 0 0', padding: 8, maxHeight: 180, overflow: 'auto',
  background: C.input, border: `1px solid ${C.border}`, borderRadius: 8,
  color: C.fg2, fontSize: '.74rem', fontFamily: mono, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
};
const row = { display: 'flex', gap: 6, alignItems: 'center' };

// The JSON body the first batch would send — mirrors build_http_requests
// (server/app/engine/steps/enrich.py) for display only; the server builds
// the real one. Returns {text} or {error}.
function firstBatchBody(c, rows) {
  const size = Number(c.batch_size);
  if (!Number.isInteger(size) || size < 1) return { error: 'Batch size must be a whole number of 1 or more.' };
  const fields = (c.fields || []).filter(Boolean);
  if (!fields.length) return { error: 'Pick at least one column for the JSON body.' };
  const keys = c.field_keys || {};
  const records = (rows || []).slice(0, size).map(r => Object.fromEntries(
    fields.map(f => [(keys[f] || '').trim() || f, r[f] === undefined ? null : r[f]]),
  ));
  const payload = (c.single_object && size === 1) ? records[0] ?? {} : records;
  const tpl = (c.body_template || '').trim();
  if (!tpl) return { text: JSON.stringify(payload, null, 2) };
  try {
    return { text: JSON.stringify(JSON.parse(tpl.replace(RECORDS_TOKEN, JSON.stringify(payload))), null, 2) };
  } catch (e) {
    return { error: `Body template isn't valid JSON: ${e.message}` };
  }
}

// ── AI Flow (http_request) ──────────────────────────────────────────────────────────
// Sends the step's rows to a web API in batches; each batch is one request
// whose JSON body holds that batch's records (chosen columns only). Every
// row is kept and gets its batch's status + response. Only a real run sends;
// the preview shows the request each batch would make.
// Mirrors s_http_request (server/app/engine/steps/enrich.py).
export function HttpRequestInspector({ step, onChange, columns, pipeline, uploadedRows }) {
  const c = step.config || {};
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const liveCols = React.useContext(ColumnsContext);
  const cols = (columns || liveCols || []).filter(col => !BOOKKEEPING.has(col.name));
  const fields = c.fields || [];
  const keys = c.field_keys || {};
  const sendBody = (c.body_type || 'records') === 'records';
  const batch = Number(c.batch_size);
  const authType = c.auth_type || 'none';

  const placeholders = React.useMemo(() => {
    const texts = [c.url || '', ...(c.params || []).map(p => String(p[1] ?? '')), ...(c.headers || []).map(h => String(h[1] ?? ''))];
    return [...new Set(texts.flatMap(t => [...t.matchAll(PLACEHOLDER_RE)].map(m => m[1])))];
  }, [c.url, c.params, c.headers]);

  const body = sendBody ? firstBatchBody(c, uploadedRows) : null;

  // "By key" is shown while either matching field is filled in, or once picked.
  const [matchByKey, setMatchByKey] = React.useState(!!(c.response_match_column || c.response_match_field));
  React.useEffect(() => { setMatchByKey(!!(c.response_match_column || c.response_match_field)); }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [test, setTest] = React.useState(null);   // {busy} | result | {error}
  React.useEffect(() => { setTest(null); }, [step.id]);
  const sendTest = async () => {
    setTest({ busy: true });
    try {
      setTest(await api.httpTest(pipeline.id, { config: c, rows: (uploadedRows || []).slice(0, 1000) }));
    } catch (e) {
      setTest({ ok: false, error: e.message });
    }
  };

  return (
    <div>
      <FieldLabel hint="Use {column} to put a row's value in the URL, a parameter or a header. That needs Batch size 1, so each row is its own request.">Request</FieldLabel>
      <div style={row}>
        <Select value={c.method || 'POST'} onChange={(v) => u({ method: v })} options={METHODS} style={{ width: 100 }} />
        <TextInput mono value={c.url} onChange={(v) => u({ url: v })} placeholder="https://api.example.com/orders" />
      </div>
      {placeholders.length > 0 && batch !== 1 && (
        <Alert kind="warn" style={{ marginTop: 6 }}>
          {placeholders.map(p => `{${p}}`).join(', ')} need Batch size 1.
        </Alert>
      )}

      <FieldLabel hint="Added to the URL as ?name=value.">Query parameters</FieldLabel>
      <KVRows entries={c.params || []} onChange={(v) => u({ params: v })} keyPlaceholder="name" valuePlaceholder="value or {column}" />

      <FieldLabel hint="Content-Type: application/json is added for a JSON body unless you set it.">Headers</FieldLabel>
      <KVRows entries={c.headers || []} onChange={(v) => u({ headers: v })} keyPlaceholder="Header-Name" valuePlaceholder="value or {column}" />

      <FieldLabel>Authentication</FieldLabel>
      <Select value={authType} onChange={(v) => u({ auth_type: v })} options={AUTH_TYPES} />
      {authType === 'bearer' && (
        <TextInput mono type="password" value={c.auth_token} onChange={(v) => u({ auth_token: v })} placeholder="Token" style={{ marginTop: 6 }} />
      )}
      {authType === 'basic' && (
        <div style={{ ...row, marginTop: 6 }}>
          <TextInput value={c.auth_user} onChange={(v) => u({ auth_user: v })} placeholder="User" />
          <TextInput type="password" value={c.auth_password} onChange={(v) => u({ auth_password: v })} placeholder="Password" />
        </div>
      )}
      {authType === 'api_key' && (
        <div style={{ ...row, marginTop: 6 }}>
          <TextInput mono value={c.api_key_name} onChange={(v) => u({ api_key_name: v })} placeholder="X-Api-Key" />
          <TextInput mono type="password" value={c.api_key_value} onChange={(v) => u({ api_key_value: v })} placeholder="Key" />
          <Select value={c.api_key_in || 'header'} onChange={(v) => u({ api_key_in: v })}
            options={[{ value: 'header', label: 'Header' }, { value: 'query', label: 'Query' }]} style={{ width: 100 }} />
        </div>
      )}
      {authType !== 'none' && (
        <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 4 }}>
          Saved with the pipeline in plain text, and included when the pipeline is exported.
        </div>
      )}

      <FieldLabel>Body</FieldLabel>
      <Segmented value={c.body_type || 'records'} onChange={(v) => u({ body_type: v })}
        options={[{ value: 'records', label: 'JSON records' }, { value: 'none', label: 'No body' }]} />

      {sendBody && (
        <>
          <FieldLabel hint="Each row becomes one JSON object with these columns, in this order.">Columns in the JSON</FieldLabel>
          <MultiCols columns={cols} selected={fields} onChange={(v) => u({ fields: v })} />
          <div style={{ ...row, marginTop: 6 }}>
            <Button kind="ghost" size="sm" onClick={() => u({ fields: cols.map(col => col.name) })}>All columns</Button>
            {fields.length > 0 && <Button kind="ghost" size="sm" onClick={() => u({ fields: [] })}>Clear</Button>}
          </div>

          {fields.length > 0 && (
            <>
              <FieldLabel hint="Leave blank to use the column name as the JSON key.">JSON keys</FieldLabel>
              {fields.map(f => (
                <div key={f} style={{ ...row, marginBottom: 6 }}>
                  <span style={{ flex: 1, minWidth: 0, color: C.fg3, fontSize: '.8rem', fontFamily: mono, overflow: 'hidden', textOverflow: 'ellipsis' }}>{f}</span>
                  <span style={{ color: C.fg5, fontSize: '.8rem' }}>→</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <TextInput mono value={keys[f] ?? ''} placeholder={f}
                      onChange={(v) => u({ field_keys: { ...keys, [f]: v } })} />
                  </div>
                </div>
              ))}
            </>
          )}

          <FieldLabel hint={`Optional. JSON around the records, with ${RECORDS_TOKEN} where the batch's array goes, e.g. {"source": "dps", "items": ${RECORDS_TOKEN}}. Blank sends the array on its own.`}>Body template</FieldLabel>
          <textarea value={c.body_template ?? ''} onChange={(e) => u({ body_template: e.target.value })}
            placeholder={`{"items": ${RECORDS_TOKEN}}`} style={textArea} spellCheck={false} />

          {batch === 1 && (
            <label style={{ ...row, marginTop: 6, color: C.fg3, fontSize: '.8rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!c.single_object} onChange={(e) => u({ single_object: e.target.checked })} />
              Send each record as an object, not a one-item array
            </label>
          )}
        </>
      )}

      <FieldLabel hint="How many rows go into each request. 10 sends rows 1-10, then 11-20, and so on.">Batching</FieldLabel>
      <div style={row}>
        <span style={{ color: C.fg4, fontSize: '.78rem', whiteSpace: 'nowrap' }}>Batch size</span>
        <TextInput mono value={c.batch_size ?? ''} onChange={(v) => u({ batch_size: v === '' ? '' : Number(v) })} placeholder="10" />
        <span style={{ color: C.fg4, fontSize: '.78rem', whiteSpace: 'nowrap' }}>Wait (ms)</span>
        <TextInput mono value={c.delay_ms ?? ''} onChange={(v) => u({ delay_ms: v === '' ? '' : Number(v) })} placeholder="0" />
      </div>
      <div style={{ ...row, marginTop: 6 }}>
        <span style={{ color: C.fg4, fontSize: '.78rem', whiteSpace: 'nowrap' }}>Timeout (s)</span>
        <TextInput mono value={c.timeout_s ?? ''} onChange={(v) => u({ timeout_s: v === '' ? '' : Number(v) })} placeholder="30" />
      </div>

      <label style={{ ...row, marginTop: 12, color: C.fg2, fontSize: '.82rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!c.poll} onChange={(e) => u({ poll: e.target.checked })} />
        Wait for the result (polling)
      </label>
      {c.poll && (
        <>
          <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 4 }}>
            For queued APIs such as RAPFlow AI Studio: when the response holds a result URL, it is
            requested (GET, same headers) until the status field shows a finished value, and that
            final response becomes the batch&apos;s result. The result URL must be on the same host.
          </div>
          <div style={{ ...row, marginTop: 6 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <FieldLabel hint="Field in the first response that holds the URL to poll. Dotted for nested, e.g. job.url.">Result URL field</FieldLabel>
              <TextInput mono value={c.poll_url_field ?? ''} onChange={(v) => u({ poll_url_field: v })} placeholder="resultUrl" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <FieldLabel hint="Field in each poll response that holds the job's state.">Status field</FieldLabel>
              <TextInput mono value={c.poll_status_field ?? ''} onChange={(v) => u({ poll_status_field: v })} placeholder="status" />
            </div>
          </div>
          <div style={{ ...row }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <FieldLabel hint="Comma-separated states that end the polling (case-insensitive).">Finished when</FieldLabel>
              <TextInput mono value={c.poll_done_values ?? ''} onChange={(v) => u({ poll_done_values: v })} placeholder="completed, failed" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <FieldLabel hint="Finished states that count as success. Any other finished state fails the batch.">Success when</FieldLabel>
              <TextInput mono value={c.poll_success_values ?? ''} onChange={(v) => u({ poll_success_values: v })} placeholder="completed" />
            </div>
          </div>
          <div style={{ ...row, marginTop: 6 }}>
            <span style={{ color: C.fg4, fontSize: '.78rem', whiteSpace: 'nowrap' }}>Check every (s)</span>
            <TextInput mono value={c.poll_interval_s ?? ''} onChange={(v) => u({ poll_interval_s: v === '' ? '' : Number(v) })} placeholder="2" />
            <span style={{ color: C.fg4, fontSize: '.78rem', whiteSpace: 'nowrap' }}>Give up after (s)</span>
            <TextInput mono value={c.poll_max_wait_s ?? ''} onChange={(v) => u({ poll_max_wait_s: v === '' ? '' : Number(v) })} placeholder="300" />
          </div>
        </>
      )}

      <FieldLabel hint="A batch fails on a non-2xx status, when the server can't be reached, or (with polling) when the job doesn't finish successfully in time. Stopping fails this step; batches already sent stay sent.">When a batch fails</FieldLabel>
      <Segmented value={c.on_error || 'continue'} onChange={(v) => u({ on_error: v })}
        options={[{ value: 'continue', label: 'Record it and continue' }, { value: 'stop', label: 'Stop' }]} />

      <FieldLabel hint="Each row gets its batch's HTTP status and response text. Leave the response column blank to skip it.">Result columns</FieldLabel>
      <div style={row}>
        <TextInput mono value={c.status_column ?? ''} onChange={(v) => u({ status_column: v })} placeholder="http_status" />
        <TextInput mono value={c.response_column ?? ''} onChange={(v) => u({ response_column: v })} placeholder="Response column (optional)" />
      </div>

      <label style={{ ...row, marginTop: 10, color: C.fg2, fontSize: '.82rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!c.response_to_columns} onChange={(e) => u({ response_to_columns: e.target.checked })} />
        Add response as columns
      </label>
      {c.response_to_columns && (
        <>
          <FieldLabel hint={'Optional. Where the result sits in the response, e.g. response.data for an AI Studio flow. Blank reads the whole response. A result sent as text (JSON in a string, or [{key=value, …}]) is read too. An object fills every row of its batch; a list with one object per row fills row by row.'}>Read from</FieldLabel>
          <TextInput mono value={c.response_path ?? ''} onChange={(v) => u({ response_path: v })} placeholder="e.g. response.data" />
          <FieldLabel hint="By position: the response list's first item goes to the batch's first row, and so on; the list must have one item per row. By key: each row gets the item whose field equals the row's column value, whatever the order (trimmed, case-insensitive, 5 = &quot;5&quot;).">Match items to rows</FieldLabel>
          <Segmented value={matchByKey ? 'key' : 'position'}
            onChange={(v) => (v === 'key'
              ? setMatchByKey(true)
              : (setMatchByKey(false), u({ response_match_column: '', response_match_field: '' })))}
            options={[{ value: 'position', label: 'By position' }, { value: 'key', label: 'By key' }]} />
          {matchByKey && (
            <div style={{ ...row, marginTop: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ColSelect columns={cols} value={c.response_match_column || ''} onChange={(v) => u({ response_match_column: v })} />
              </div>
              <span style={{ color: C.fg5, fontSize: '.8rem' }}>=</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <TextInput mono value={c.response_match_field ?? ''} onChange={(v) => u({ response_match_field: v })} placeholder="Response field, e.g. orderNo" />
              </div>
            </div>
          )}
          {matchByKey && !(c.response_match_column && (c.response_match_field || '').trim()) && (
            <Alert kind="warn" style={{ marginTop: 6 }}>Pick the row column and type the response field that holds the same value.</Alert>
          )}
          <FieldLabel hint="Optional. Put in front of every response column name, to keep them apart from existing columns.">Column prefix</FieldLabel>
          <TextInput mono value={c.response_prefix ?? ''} onChange={(v) => u({ response_prefix: v })} placeholder="none (e.g. api_)" />
          <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 4 }}>
            Nested objects become dotted names (customer.id); lists are kept as JSON text.
            The columns appear after a run, since the preview sends nothing. Use Send test request to see them.
          </div>
        </>
      )}

      {sendBody && (
        <>
          <FieldLabel hint="Built from the rows reaching this step in the preview.">First batch body</FieldLabel>
          {body?.error
            ? <Alert kind="warn">{body.error}</Alert>
            : <pre style={pre}>{body?.text}</pre>}
        </>
      )}

      <div style={{ marginTop: 12 }}>
        <Button kind="ghost" size="sm" icon={<Send size={13} />} disabled={!c.url || test?.busy || !pipeline?.id}
          onClick={sendTest} title="Really sends the first batch to the API. The 'Apply only when' condition is not applied.">
          {test?.busy ? (c.poll ? 'Sending and waiting for the result…' : 'Sending…') : 'Send test request'}
        </Button>
      </div>
      {test && !test.busy && (
        test.status == null && test.error && !test.request
          ? <Alert kind="error" style={{ marginTop: 8 }}>{test.error}</Alert>
          : (
            <div style={{ marginTop: 8 }}>
              <Alert kind={test.ok ? 'ok' : 'error'}>
                {test.status != null ? `HTTP ${test.status}` : test.error} · {test.rowsSent} row{test.rowsSent === 1 ? '' : 's'} · {test.elapsedMs} ms
              </Alert>
              {test.error && test.status != null && <Alert kind="error" style={{ marginTop: 6 }}>{test.error}</Alert>}
              {test.columns && (
                <div style={{ color: C.fg3, fontSize: '.78rem', marginTop: 6 }}>
                  Response columns: <span style={{ fontFamily: mono }}>{test.columns.join(', ') || '(none)'}</span>
                </div>
              )}
              {test.matched && (
                <div style={{ color: C.fg3, fontSize: '.78rem', marginTop: 4 }}>
                  Matched {test.matched.rows} of {test.rowsSent} rows
                  {test.matched.duplicates ? ` · ${test.matched.duplicates} duplicate key(s)` : ''}
                  {test.matched.extra ? ` · ${test.matched.extra} item(s) matched no row` : ''}
                </div>
              )}
              {test.columnsProblem && <Alert kind="warn" style={{ marginTop: 6 }}>{test.columnsProblem}</Alert>}
              {test.response && <pre style={pre}>{test.response}{test.truncated ? '\n…' : ''}</pre>}
            </div>
          )
      )}

      <Alert kind="info" style={{ marginTop: 12 }}>
        Nothing is sent while you edit: the preview shows &ldquo;not sent (preview)&rdquo; and the
        request each batch would make. Requests go out when the pipeline runs.
      </Alert>
    </div>
  );
}
